import _findIndex from 'lodash/findIndex';
import createReducer from '@sift/resift/createReducer';

import { LIST_SLIDE_KEY, CHEERS_SLIDE_KEY } from 'components/onboarding/constants';
import reorderTasks from 'components/onboarding/reorderTasks';
import * as Types from 'components/onboarding/typings';

export interface State {
  bubbleOpen: boolean;
  checkList: Types.TaskCategoryList;
  slideKey: string; // LIST_SLIDE_KEY | CHEERS_SLIDE_KEY | taskId;
  prevSlideKey: string; // LIST_SLIDE_KEY | CHEERS_SLIDE_KEY | taskId;
  taskIdInFocus: string | null; // use in completion cheers;
  bubbleSeenByUser: boolean;
}

// ------------------------------------
// Constants
// ------------------------------------
export const TOGGLE_ONBOARDING_BUBBLE_OPEN = 'TOGGLE_ONBOARDING_BUBBLE_OPEN';
export const TOGGLE_CATEGORY_EXPANSION = 'TOGGLE_CATEGORY_EXPANSION';
export const TOGGLE_TASK_COMPLETION = 'TOGGLE_TASK_COMPLETION';
export const SET_ONBOARDING_TASKS = 'SET_ONBOARDING_TASKS';
export const SET_SLIDE_KEY = 'SET_SLIDE_KEY';
export const SET_TASK_ID_IN_FOCUS = 'SET_TASK_ID_IN_FOCUS';
export const SET_BUBBLE_SEEN_BY_USER = 'SET_BUBBLE_SEEN_BY_USER';

// ------------------------------------
// Actions
// ------------------------------------
export const toggleBubbleOpen = () => {
  return {
    type: TOGGLE_ONBOARDING_BUBBLE_OPEN,
    identifier: 'onboarding',
  };
};

export const toggleCategoryExpansion = (categoryId: string) => {
  return {
    type: TOGGLE_CATEGORY_EXPANSION,
    identifier: 'onboarding',
    payload: {
      categoryId,
    },
  };
};

export const toggleTaskCompletion = (categoryId: string, taskId: string) => {
  return {
    type: TOGGLE_TASK_COMPLETION,
    identifier: 'onboarding',
    payload: {
      categoryId,
      taskId,
    },
  };
};

export const setOnboardingTasks = (onboardingTasks: Types.TaskCategoryList) => {
  return {
    type: SET_ONBOARDING_TASKS,
    identifier: 'onboarding',
    payload: {
      onboardingTasks,
    },
  };
};

export const setSlideKey = (slideKey: string) => {
  return {
    type: SET_SLIDE_KEY,
    identifier: 'onboarding',
    payload: {
      slideKey,
      prevSlideKey: slideKey,
    },
  };
};

export const setTaskIdInFocus = (taskIdInFocus: string) => {
  return {
    type: SET_TASK_ID_IN_FOCUS,
    identifier: 'onboarding',
    payload: {
      taskIdInFocus,
    },
  };
};

export const setBubbleSeenByUser = () => {
  return {
    type: SET_BUBBLE_SEEN_BY_USER,
    identifier: 'onboarding',
  };
};

// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
  [TOGGLE_ONBOARDING_BUBBLE_OPEN]: (state: State) => {
    return {
      ...state,
      bubbleOpen: !state.bubbleOpen,
    };
  },

  [TOGGLE_CATEGORY_EXPANSION]: (state: State, action: any) => {
    const categoryId = action.payload.categoryId;
    const index = _findIndex(state.checkList, item => item.id === categoryId);

    if (index === -1) {
      return state;
    }

    return {
      ...state,
      checkList: state.checkList.map((item, i) => {
        if (i === index) {
          return { ...item, isExpanded: !item.isExpanded };
        }

        return item;
      }),
    };
  },

  [TOGGLE_TASK_COMPLETION]: (state: State, action: any) => {
    const categoryId = action.payload.categoryId;
    const taskId = action.payload.taskId;

    return {
      ...state,
      slideKey: CHEERS_SLIDE_KEY,
      taskIdInFocus: taskId,
      checkList: reorderTasks(
        state.checkList.map(category => {
          if (category.id === categoryId) {
            return {
              ...category,
              tasks: category.tasks.map(t => {
                if (t.id === taskId) {
                  return {
                    ...t,
                    isComplete: !t.isComplete,
                  };
                }

                return t;
              }),
            };
          }

          return category;
        }),
      ),
    };
  },

  [SET_ONBOARDING_TASKS]: (state: State, action: any) => {
    return {
      ...state,
      checkList: reorderTasks(action.payload.onboardingTasks),
    };
  },

  [SET_SLIDE_KEY]: (state: State, action: any) => {
    return {
      ...state,
      slideKey: action.payload.slideKey,
      prevSlideKey: action.payload.prevSlideKey,
      bubbleOpen: true,
    };
  },

  [SET_BUBBLE_SEEN_BY_USER]: (state: State) => {
    return {
      ...state,
      bubbleSeenByUser: true,
    };
  },
};

// ------------------------------------
// Reducer
// ------------------------------------
const initialState: State = {
  bubbleOpen: false,
  checkList: [],
  slideKey: LIST_SLIDE_KEY,
  prevSlideKey: '',
  taskIdInFocus: null,
  bubbleSeenByUser: false,
};

export default createReducer(initialState, ACTION_HANDLERS);
